import React from "react"
import {useTranslation} from "react-i18next"

// Models
// aliased: the model is called `Record`, which would shadow TS's built-in Record<K,V>
import {Record as RecordModel} from "../../../models"

// Styles
import useStyles from "./styles"

type ModernClinicalDialogProps = {
    open: boolean
    record: RecordModel | null
    onClose(): void
}

/**
 * Themed replacement for the legacy <InitDialog/> when the Modern skin is
 * active. Same data, same i18n intent — but the prototype's design instead of
 * a raw MUI Dialog with an OK button.
 *
 * Classic keeps using InitDialog untouched.
 *
 * Fields follow PRODUCTION, not the prototype: the prototype's "Referring" row
 * has no production equivalent and is dropped rather than faked, and
 * `pacemaker` (which production has and the prototype does not) is added.
 */
export default function ModernClinicalDialog(props: ModernClinicalDialogProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {open, record, onClose} = props;

    // Escape closes, matching every other dismissable surface in the app
    React.useEffect(() => {
        if (!open) {
            return
        }

        function onKeyDown(e: KeyboardEvent) {
            if (e.key === "Escape") {
                e.stopPropagation();
                onClose();
            }
        }

        window.addEventListener("keydown", onKeyDown);

        return () => window.removeEventListener("keydown", onKeyDown)
    }, [open, onClose]);

    if (!open || record === null || !record.metaData) {
        return null
    }

    const meta = record.metaData;

    // the API stores these URL-encoded
    const decode = (value: string | null | undefined): string =>
      value ? value.split("%20").join(" ") : "—";

    const yesNo = (state: boolean) => state ? t("modern.clinical.yes") : t("modern.clinical.no");

    const flags: Array<{labelKey: string, value: boolean}> = [
        {labelKey: "modern.clinical.diabetes", value: !!meta.diabetes},
        {labelKey: "modern.clinical.antihypertensive", value: !!meta.antihypertensive},
        {labelKey: "modern.clinical.dyslipidemia", value: !!meta.dyslipidemia},
        {labelKey: "modern.clinical.pacemaker", value: !!meta.pacemaker},
    ];

    return (
      <div
        className={styles.overlay}
        onClick={(e) => {
            if (e.target === e.currentTarget) {
                onClose()
            }
        }}
      >
          <div className={styles.card} role="dialog" aria-modal="true" aria-label={t("modern.clinical.title")}>
              <div className={styles.card_head}>
                  <span className={styles.microlabel}>{t("modern.clinical.title")}</span>
                  <button type="button" className={styles.close} onClick={onClose} aria-label={t("modern.close")}>
                      ×
                  </button>
              </div>

              <div className={styles.meta_row}>
                  <span className={styles.meta_label}>{t("modern.clinical.exam_reason")}</span>
                  <span className={styles.meta_val}>{decode(meta.reason)}</span>
              </div>

              {flags.map(flag => (
                <div key={flag.labelKey} className={styles.meta_row}>
                    <span className={styles.meta_label}>{t(flag.labelKey)}</span>
                    <span
                      className={`${styles.meta_val} ${flag.value ? styles.meta_val__yes : styles.meta_val__no}`}
                    >
                        {yesNo(flag.value)}
                    </span>
                </div>
              ))}

              <div className={styles.meta_row}>
                  <span className={styles.meta_label}>{t("modern.clinical.note")}</span>
                  <span className={`${styles.meta_val} ${styles.meta_val__note}`}>{decode(meta.note)}</span>
              </div>
          </div>
      </div>
    )
}
